import { api, apiErrorMessage, useAuthStore } from '@bemor/web-kit';
import {
    Badge,
    Button,
    Card,
    CardContent,
    CardHeader,
    CardTitle,
    DateTime,
    HttpStatusBadge,
    Skeleton,
    StatusBadge,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@bemor/web-kit/ui';

import type {
    Dispute,
    StaffAttempt,
    StaffPeriod,
    StaffWebhookDelivery,
    WebhookDelivery,
} from '@/api/types';
import { P } from '@/lib/permissions';
import { useList, useWrite } from '@/lib/queries';

/** One object a merchant webhook is about (bm-147 § 7, K-1): its delivery's `subject_type` and `subject_id`. */
export type DeliverySubject = {
    type: NonNullable<StaffWebhookDelivery['subject_type']>;
    id: string;
};

const PATH = '/staff/webhook-deliveries';
// bm-147 § 7: the route takes at most 100 subject pairs (422 above that)
const MAX_PAIRS = 100;

/**
 * bm-147 § 8 (K-2, D-20): the subjects the order page and the charge page both pass: the order, the charge, the
 * charge's refunds (the staff charge and order shapes carry them, E-23) and its disputes, read from the staff disputes
 * list searched by the charge id (E-21). Undefined while the disputes load, so the panel asks once. Without
 * risk.disputes.read (or delivery.webhooks.manage, when no panel shows) it reads no disputes and passes none.
 * Call it at page level: it is a per-record list read (the route prefetch rule). A failed disputes read keeps the other
 * subjects and comes back as `disputesError`, which the panel shows, so a set without the disputes never reads complete.
 */
export type ChargeSubjects = {
    subjects: DeliverySubject[] | undefined;
    disputesError: Error | null;
};

export function useChargeSubjects(
    link:
        | {
              orderId: string | null | undefined;
              chargeId: string | null | undefined;
              refunds: readonly { id: string }[];
          }
        | undefined,
): ChargeSubjects {
    const can = useAuthStore((s) => s.can);
    const chargeId = link?.chargeId ?? '';
    const readsDisputes =
        can(P.webhooksManage) && can(P.disputesRead) && chargeId !== '';
    // the search matches ids as substrings, so only this charge's rows are kept below
    const disputes = useList<Dispute>(
        '/staff/disputes',
        { q: chargeId, per_page: MAX_PAIRS },
        readsDisputes,
    );
    const disputesError = readsDisputes ? disputes.error : null;
    if (!link || (readsDisputes && disputes.isPending))
        return { subjects: undefined, disputesError };
    return {
        subjects: [
            ...(link.orderId
                ? [{ type: 'order' as const, id: link.orderId }]
                : []),
            ...(chargeId ? [{ type: 'charge' as const, id: chargeId }] : []),
            ...link.refunds.map((r) => ({ type: 'refund' as const, id: r.id })),
            ...(readsDisputes ? (disputes.data?.data.items ?? []) : [])
                .filter((d) => d.charge_id === chargeId)
                .map((d) => ({ type: 'dispute' as const, id: d.id })),
        ],
        disputesError,
    };
}

/**
 * bm-147 § 8 (K-2): the subscription, then each period newest first with its order, its charge and every attempt's
 * resolved charge (D-3, D-19), each pair once. Past the route's 100 pairs it keeps the subscription and the newest
 * whole periods, and says so.
 */
export function subscriptionSubjects(
    id: string,
    periods: readonly StaffPeriod[],
): { subjects: DeliverySubject[]; truncated: boolean } {
    const subjects: DeliverySubject[] = [{ type: 'subscription', id }];
    const seen = new Set([`subscription:${id}`]);
    for (const p of periods) {
        // the allOf intersection indexes as the base Attempt, so name the staff row type (as the periods table does)
        const log: readonly StaffAttempt[] = p.attempts_log ?? [];
        const own = (
            [
                ['order', p.order_id],
                ['charge', p.charge_id],
                ...log.map((a) => ['charge', a.charge_id] as const),
            ] as const
        ).flatMap(([type, sid]): DeliverySubject[] => {
            const key = `${type}:${sid ?? ''}`;
            if (!sid || seen.has(key)) return [];
            seen.add(key);
            return [{ type, id: sid }];
        });
        if (subjects.length + own.length > MAX_PAIRS)
            return { subjects, truncated: true };
        subjects.push(...own);
    }
    return { subjects, truncated: false };
}

/**
 * bm-147 § 7 and § 8 (D-16): the merchant webhook deliveries of the given subjects, newest first, from
 * `GET /staff/webhook-deliveries?subject[]=<type>:<id>`, each with its event, status, HTTP code, attempts and replay
 * marker, and the existing staff Replay. Only staff holding delivery.webhooks.manage see it; for anyone else it
 * renders nothing and reads nothing. `subjects` undefined: the page is still building its set (a skeleton).
 * `disputesError`: the disputes could not be read, so their deliveries are missing and the panel says so.
 */
export function WebhookDeliveries({
    subjects,
    disputesError = null,
    notice,
}: {
    subjects: readonly DeliverySubject[] | undefined;
    disputesError?: Error | null;
    notice?: string;
}) {
    const manages = useAuthStore((s) => s.can)(P.webhooksManage);
    const pairs = (subjects ?? []).map((s) => `${s.type}:${s.id}`);
    const list = useList<StaffWebhookDelivery>(
        PATH,
        { subject: pairs },
        manages && pairs.length > 0,
    );
    const replay = useWrite(
        async (row: StaffWebhookDelivery) =>
            (
                await api().post<WebhookDelivery>(
                    `/staff/webhook-endpoints/${row.endpoint_id ?? ''}/deliveries/${row.id}/replay`,
                )
            ).data,
        [PATH],
    );
    if (!manages || subjects?.length === 0) return null;
    const rows = list.data?.data.items ?? [];
    return (
        <Card data-testid="webhook-deliveries">
            <CardHeader>
                <CardTitle className="text-base">Webhook deliveries</CardTitle>
            </CardHeader>
            <CardContent>
                {disputesError ? (
                    <p
                        role="alert"
                        className="text-destructive mb-3 text-sm"
                        data-testid="webhook-deliveries-disputes-error"
                    >
                        The disputes could not be read, so their deliveries are
                        not listed: {apiErrorMessage(disputesError)}
                    </p>
                ) : null}
                {list.error ? (
                    <p role="alert" className="text-destructive text-sm">
                        {apiErrorMessage(list.error)}
                    </p>
                ) : subjects === undefined || list.isPending ? (
                    <Skeleton className="h-24 w-full" />
                ) : rows.length === 0 ? (
                    <p className="text-muted-foreground text-sm">
                        No webhook deliveries yet.
                    </p>
                ) : (
                    <div className="overflow-x-auto">
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>Time</TableHead>
                                    <TableHead>Event</TableHead>
                                    <TableHead>Status</TableHead>
                                    <TableHead>HTTP code</TableHead>
                                    <TableHead>Attempts</TableHead>
                                    <TableHead>
                                        <span className="sr-only">Replay</span>
                                    </TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {rows.map((r) => (
                                    <TableRow
                                        key={r.id}
                                        data-testid="webhook-delivery"
                                    >
                                        <TableCell className="whitespace-nowrap">
                                            <DateTime value={r.created_at} />
                                        </TableCell>
                                        <TableCell className="font-mono text-xs">
                                            {r.event_type}
                                            {r.replay ? (
                                                <Badge
                                                    variant="outline"
                                                    className="ml-2"
                                                    data-testid="replay-marker"
                                                >
                                                    Replay
                                                </Badge>
                                            ) : null}
                                        </TableCell>
                                        <TableCell>
                                            <StatusBadge
                                                status={r.status}
                                                domain="delivery"
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <HttpStatusBadge
                                                status={r.http_status ?? null}
                                            />
                                        </TableCell>
                                        <TableCell className="tabular-nums">
                                            {r.attempts}
                                        </TableCell>
                                        <TableCell>
                                            {r.endpoint_id ? (
                                                <Button
                                                    size="sm"
                                                    variant="outline"
                                                    aria-label={`Replay ${r.id}`}
                                                    disabled={replay.isPending}
                                                    onClick={() =>
                                                        replay.mutate(r)
                                                    }
                                                >
                                                    Replay
                                                </Button>
                                            ) : null}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </div>
                )}
                {notice ? (
                    <p className="text-muted-foreground mt-3 text-xs">
                        {notice}
                    </p>
                ) : null}
                {replay.error ? (
                    <p role="alert" className="text-destructive mt-3 text-xs">
                        {apiErrorMessage(replay.error)}
                    </p>
                ) : null}
            </CardContent>
        </Card>
    );
}
